import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import api from '@/lib/api';
import { formatApiError } from '@/lib/validators';
import { toast } from 'sonner';
import { Layers, Plus, Edit2, Trash2, Search, Cog, RefreshCw } from 'lucide-react';

import { Screen, Pane, CommandBar, StatStrip, DataGrid, CellButton, SidePane } from '@/components/data';

// Stage categories with colors
const CATEGORY_COLORS = {
  'Pre-Press': { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
  'Printing': { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  'Finishing': { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  'Cutting': { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
  'Corrugation': { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200' },
  'Pasting': { bg: 'bg-pink-50', text: 'text-pink-700', border: 'border-pink-200' },
  'Assembly': { bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200' },
  'Final': { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
};

export default function ProductionStagesPage() {
  const [stages, setStages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [activeFilter, setActiveFilter] = useState('all');
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [machinesDialogOpen, setMachinesDialogOpen] = useState(false);
  
  // Form states
  const [formData, setFormData] = useState({ name: '', category: 'Printing', notes: '', supports_external_vendor: false });
  const [editingStage, setEditingStage] = useState(null);
  const [deletingStage, setDeletingStage] = useState(null);
  const [viewingMachines, setViewingMachines] = useState(null);
  const [linkedMachines, setLinkedMachines] = useState([]);
  const [saving, setSaving] = useState(false);

  // Fetch stages
  const fetchStages = useCallback(async () => {
    try {
      const params = {};
      if (categoryFilter !== 'all') params.category = categoryFilter;
      if (activeFilter !== 'all') params.is_active = activeFilter === 'active';
      if (search) params.search = search;
      
      const res = await api.get('/stages', { params });
      // Ensure we always have an array
      const data = Array.isArray(res.data) ? res.data : (res.data?.stages || []);
      setStages(data);
    } catch (e) {
      toast.error('Failed to load stages');
      setStages([]);
    }
  }, [categoryFilter, activeFilter, search]);

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const res = await api.get('/stages/categories/list');
      setCategories(res.data.categories || []);
    } catch (e) {
    }
  }, []);

  useEffect(() => {
    Promise.all([fetchStages(), fetchCategories()]).then(() => setLoading(false));
  }, [fetchStages, fetchCategories]);

  // Create stage
  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error('Stage name is required');
      return;
    }
    setSaving(true);
    try {
      await api.post('/stages', {
        name: formData.name.trim(),
        category: formData.category,
        notes: formData.notes || null,
        supports_external_vendor: formData.supports_external_vendor,
      });
      toast.success('Stage created successfully');
      setCreateDialogOpen(false);
      setFormData({ name: '', category: 'Printing', notes: '', supports_external_vendor: false });
      fetchStages();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to create stage'));
    }
    setSaving(false);
  };

  // Edit stage
  const openEditDialog = (stage) => {
    setEditingStage(stage);
    setFormData({
      name: stage.name,
      category: stage.category,
      notes: stage.notes || '',
      supports_external_vendor: stage.supports_external_vendor || false,
    });
    setEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    if (!formData.name.trim()) {
      toast.error('Stage name is required');
      return;
    }
    setSaving(true);
    try {
      await api.put(`/stages/${editingStage.id}`, {
        name: formData.name.trim(),
        category: formData.category,
        notes: formData.notes || null,
        supports_external_vendor: formData.supports_external_vendor,
      });
      toast.success('Stage updated successfully');
      setEditDialogOpen(false);
      setEditingStage(null);
      fetchStages();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to update stage'));
    }
    setSaving(false);
  };

  // Delete/deactivate stage
  const handleDelete = async () => {
    setSaving(true);
    try {
      const res = await api.delete(`/stages/${deletingStage.id}`);
      toast.success(res.data.message);
      if (res.data.warning) {
        toast.warning(res.data.warning);
      }
      setDeleteDialogOpen(false);
      setDeletingStage(null);
      fetchStages();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to deactivate stage');
    }
    setSaving(false);
  };

  // View linked machines
  const viewMachines = async (stage) => {
    setViewingMachines(stage);
    try {
      const res = await api.get(`/stages/${stage.id}/machines`);
      setLinkedMachines(res.data.machines || []);
    } catch (e) {
      setLinkedMachines([]);
    }
    setMachinesDialogOpen(true);
  };

  // Toggle active status
  const toggleActive = async (stage) => {
    try {
      await api.put(`/stages/${stage.id}`, { is_active: !stage.is_active });
      toast.success(`Stage ${stage.is_active ? 'deactivated' : 'activated'}`);
      fetchStages();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update stage');
    }
  };

  // Get category badge styles
  const getCategoryStyles = (category) => {
    return CATEGORY_COLORS[category] || { bg: 'bg-slate-50', text: 'text-slate-700', border: 'border-slate-200' };
  };

  // Filter stages
  const filteredStages = stages.filter(stage => {
    if (search && !stage.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  // Group by category for display
  const stagesByCategory = filteredStages.reduce((acc, stage) => {
    const cat = stage.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(stage);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-brand-blue" />
      </div>
    );
  }

  const columns = [
    {
      key: 'name',
      header: 'Stage',
      width: 'minmax(13rem,1.5fr)',
      render: (s) => (
        <>
          <span className={`block truncate font-semibold ${s.is_active === false ? 'text-slate-400' : 'text-slate-900'}`}>
            {s.name || s.stage_name}
          </span>
          {s.notes && <span className="block truncate text-[11px] leading-tight text-slate-400">{s.notes}</span>}
        </>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      width: '9rem',
      render: (s) => {
        const styles = getCategoryStyles(s.category);
        return (
          <span className={`inline-flex items-center gap-1.5 text-[12px] font-medium ${styles.text}`}>
            <span className={`h-2 w-2 rounded-full ${styles.text.replace('text-', 'bg-')}`} />
            {s.category}
          </span>
        );
      },
    },
    {
      key: 'slug',
      header: 'Key',
      width: '10rem',
      render: (s) => <span className="block truncate font-mono text-[11px] text-slate-400">{s.slug || s.stage_key || '—'}</span>,
    },
    {
      key: 'supports_external_vendor',
      header: 'Can go out',
      width: '7rem',
      render: (s) => (
        <span className={`text-[12px] font-medium ${s.supports_external_vendor ? 'text-amber-600' : 'text-slate-400'}`}>
          {s.supports_external_vendor ? 'Yes' : 'No'}
        </span>
      ),
    },
    {
      key: 'is_active',
      header: 'Status',
      width: '6rem',
      render: (s) => (
        <span className={`text-[12px] font-medium ${s.is_active === false ? 'text-slate-400' : 'text-emerald-600'}`}>
          {s.is_active === false ? 'inactive' : 'active'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '8rem',
      align: 'right',
      render: (s) => (
        <span className="flex items-center justify-end gap-0.5">
          <CellButton icon={Cog} label="Linked machines" iconOnly onClick={() => viewMachines(s)} />
          <CellButton icon={Edit2} label="Edit stage" iconOnly onClick={() => openEditDialog(s)} />
          <CellButton icon={Trash2} label="Delete stage" iconOnly tone="danger"
            onClick={() => { setDeletingStage(s); setDeleteDialogOpen(true); }} />
        </span>
      ),
    },
  ];

  // Flat list, grouped by category with a header row — the category is a fact
  // about the stage, not a reason to build eight separate cards.
  const rows = [];
  for (const [category, categoryStages] of Object.entries(stagesByCategory)) {
    rows.push({ id: `cat:${category}`, __full: true, __render: () => {
      const styles = getCategoryStyles(category);
      return (
        <span className={`flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider ${styles.text}`}>
          <span className={`h-2 w-2 rounded-full ${styles.text.replace('text-', 'bg-')}`} />
          {category}
          <span className="font-normal normal-case tracking-normal text-slate-400">
            {categoryStages.length} stage{categoryStages.length === 1 ? '' : 's'}
          </span>
        </span>
      );
    } });
    for (const stage of categoryStages) rows.push({ ...stage, __depth: 1 });
  }

  const isEditing = editDialogOpen || createDialogOpen;

  return (
    <Screen data-testid="production-stages-page">
      <CommandBar
        title="Production Stages"
        subtitle={`${stages.length} stage${stages.length === 1 ? '' : 's'} the floor can run`}
        actions={[
          { key: 'refresh', label: 'Refresh', icon: RefreshCw, onClick: fetchStages },
          {
            key: 'add', label: 'Add Stage', icon: Plus, tone: 'accent',
            onClick: () => {
              setFormData({ name: '', category: 'Printing', notes: '', supports_external_vendor: false });
              setCreateDialogOpen(true);
            },
          },
        ]}
      >
        <div className="relative w-full max-w-xs">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <Input
            className="h-7 pl-8 text-xs"
            placeholder="Search stages…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </CommandBar>

      <StatStrip
        stats={[
          { key: null, label: 'stages', value: stages.length, tone: 'blue' },
          { key: null, label: 'categories', value: Object.keys(stagesByCategory).length },
          { key: null, label: 'can be outsourced', value: stages.filter(s => s.supports_external_vendor).length, tone: 'warn' },
        ]}
      >
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="h-7 w-40 text-xs"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all" className="text-xs">All categories</SelectItem>
            {categories.map(cat => <SelectItem key={cat} value={cat} className="text-xs">{cat}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={activeFilter} onValueChange={setActiveFilter}>
          <SelectTrigger className="h-7 w-32 text-xs"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all" className="text-xs">All status</SelectItem>
            <SelectItem value="active" className="text-xs">Active</SelectItem>
            <SelectItem value="inactive" className="text-xs">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </StatStrip>

      <Pane title="Stages" aside={`${filteredStages.length} shown`} scroll={false} bodyClassName="flex flex-col">
        <DataGrid
          data-testid="stages-table"
          columns={columns}
          rows={rows}
          getRowId={(s) => s.id}
          empty={
            <span className="flex flex-col items-center gap-2 text-slate-400">
              <Layers className="h-8 w-8 text-slate-300" />
              No stages found
            </span>
          }
        />
      </Pane>

      <SidePane
        open={isEditing}
        onClose={() => { setCreateDialogOpen(false); setEditDialogOpen(false); setEditingStage(null); }}
        title={createDialogOpen ? 'Add production stage' : `Edit ${editingStage?.name || 'stage'}`}
        subtitle={createDialogOpen ? 'It becomes selectable on every product type' : editingStage?.slug}
        width="24rem"
        footer={
          <>
            <Button variant="outline" size="sm"
              onClick={() => { setCreateDialogOpen(false); setEditDialogOpen(false); setEditingStage(null); }}>
              Cancel
            </Button>
            <Button size="sm" disabled={saving} onClick={createDialogOpen ? handleCreate : handleUpdate}
              className="bg-brand-blue text-white hover:bg-brand-blue-dark">
              {saving ? 'Saving…' : createDialogOpen ? 'Create stage' : 'Save changes'}
            </Button>
          </>
        }
      >
        <div className="space-y-2.5 p-3">
          <label className="block">
            <span className="text-[11px] font-medium text-slate-600">Stage name *</span>
            <Input className="mt-0.5 h-10 text-base sm:h-8 sm:text-sm" value={formData.name}
              placeholder="e.g. Spot UV"
              onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} />
          </label>
          <label className="block">
            <span className="text-[11px] font-medium text-slate-600">Category</span>
            <Select value={formData.category} onValueChange={v => setFormData(p => ({ ...p, category: v }))}>
              <SelectTrigger className="mt-0.5 h-10 text-base sm:h-8 sm:text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.keys(CATEGORY_COLORS).map(cat => (
                  <SelectItem key={cat} value={cat} className="text-xs">{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <label className="flex items-center justify-between gap-2 rounded border border-slate-200 px-2 py-1.5">
            <span className="min-w-0">
              <span className="block text-[12px] font-medium text-slate-700">Can be sent to a vendor</span>
              <span className="block text-[10px] text-slate-400">Shows this stage in External Processing</span>
            </span>
            <Switch
              checked={formData.supports_external_vendor}
              onCheckedChange={v => setFormData(p => ({ ...p, supports_external_vendor: v }))}
            />
          </label>
          <label className="block">
            <span className="text-[11px] font-medium text-slate-600">Notes</span>
            <Textarea className="mt-0.5 resize-none text-sm" rows={2} value={formData.notes}
              placeholder="What this stage is on the floor"
              onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))} />
          </label>
        </div>
      </SidePane>

      <SidePane
        open={machinesDialogOpen}
        onClose={() => setMachinesDialogOpen(false)}
        title={`Machines at ${viewingMachines?.name || ''}`}
        subtitle={`${linkedMachines.length} linked`}
        width="22rem"
      >
        {linkedMachines.length === 0 ? (
          <p className="p-4 text-center text-xs text-slate-400">
            No machines are linked to this stage yet. Link them from the Machines page.
          </p>
        ) : linkedMachines.map(m => (
          <div key={m.id} className="flex items-center justify-between gap-2 border-b border-slate-100 px-2.5 py-1.5 last:border-b-0">
            <span className="min-w-0">
              <span className="block truncate text-[13px] font-medium text-slate-700">{m.machine_name}</span>
              <span className="block truncate text-[10px] text-slate-400">{m.username}</span>
            </span>
            <span className={`shrink-0 text-[11px] font-medium ${m.status === 'active' ? 'text-emerald-600' : 'text-slate-400'}`}>
              {m.status}
            </span>
          </div>
        ))}
      </SidePane>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{deletingStage?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              Product types that route through this stage will lose it. Jobs already standing at it are unaffected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={saving}
              className="bg-red-600 text-white hover:bg-red-700">
              {saving ? 'Deleting…' : 'Delete stage'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Screen>
  );
}
